import { lazy, Suspense, useMemo } from "react";

import { classifyRoom } from "../../pages/Admin/Dashboard/roomClassification";
import { selectExpectedPace } from "./expectedPace";
import { useFacilitatorContext } from "./FacilitatorContextProvider";
import {
  FacilitatorDashboardTabs,
  NAV_LOCK_EXPLAINER_ID,
} from "./FacilitatorDashboardTabs";
import { evaluatePaceWriteEligibility } from "./paceWriteEligibility";
import { Button, Skeleton, SurfaceCard } from "./primitives";
import { RoomPulse } from "./RoomPulse";
import { statusTerm } from "./statusVocabulary";

const PaceControlPopover = lazy(() => import("./PaceControlPopover"));

/**
 * THE BAND STATES ONE ROOM ON BOTH MODES (FE-9.5, the ADR-009 violation).
 *
 * It used to state two. The squad set the level spine and its tallies were
 * computed over was the period-FILTERED list on `/admin/dashboard` and the FULL
 * list on `/admin/prep`, and the period selector was suppressed on Prepare
 * besides. So a facilitator filtered to period 2 pressed "prepare", watched
 * every number in the band change, and had no control on screen that could
 * explain why. ADR-009's whole decision is that Prepare and Coach are two modes
 * of ONE dashboard; a persistent band that silently means two different rooms
 * depending on which mode you are in is the sharpest available contradiction of
 * it.
 *
 * THE FILTER APPLIES ON BOTH, RATHER THAN BEING DECLARED ON BOTH. Two designs
 * closed the finding: apply the period everywhere the band renders, or leave
 * the split and label it ("across all periods" on Prepare, "period 2" on
 * Coach). The second keeps the defect and asks the facilitator to hold two
 * rooms in mind at once, which is precisely the fragmentation ADR-009 was
 * written to end. The period is DASHBOARD context, not Coach state: it lives on
 * the shell provider, it survives the mode switch already, and the only thing
 * that did not follow it across was this computation. So the filter applies
 * wherever the band renders, and `FacilitatorContextBar` shows the control that
 * explains it on the same two routes.
 *
 * NOTHING ELSE MOVES. `filterSquads` is `Array.prototype.filter` over the
 * server's list, so this is a view and never a re-order or a re-count, and the
 * fallback to the unfiltered list is kept for a provider that has not published
 * one yet.
 */

export const FacilitatorCommandBand = () => {
  const shell = useFacilitatorContext();
  const envelope = shell?.envelope;
  const pace = selectExpectedPace(envelope);
  const sourceSquads =
    shell?.filterSquads?.(envelope?.squads) ?? envelope?.squads;

  const classification = useMemo(
    () => classifyRoom(sourceSquads, envelope?.capsules, pace?.resolvedLevel),
    [sourceSquads, envelope?.capsules, pace?.resolvedLevel],
  );

  /**
   * A SECOND CLASSIFICATION, AND IT ANSWERS A DIFFERENT QUESTION.
   *
   * The one above is what the facilitator is LOOKING AT: the spine, period
   * filtered like everything else on the surface. This one is what a pace WRITE
   * WOULD DO, and the write is teacher-global. `POST set-current-level` takes
   * one value for the actor and is not scoped by cohort, challenge or period
   * (ADR-009's own consequence list says so), so the impact of confirming it
   * reaches every squad in the room and not just the slice on screen. A preview
   * computed over the slice would understate the consequences of a global
   * write, which is the one thing a confirmation must never do.
   *
   * IT ALSO CLOSES A MISMATCH THAT WAS ALREADY THERE. The popover's "after"
   * column has always been `classifyRoom(envelope.squads, ...)`, unfiltered,
   * while the "now" column was whatever the caller passed; on Coach that was
   * the filtered classification, so with a period active the two columns of one
   * impact table were computed over two different rooms and the deltas were
   * arithmetic between them. Both sides are the whole room now.
   *
   * AND IT IS WHAT KEEPS THE THREE MOUNTS EQUAL. `ManagePaceSection` passes the
   * unfiltered room (ruling D2: one behaviour, two mounts). Before FE-9.5 the
   * band happened to agree with it on `/admin/prep` only because the band did
   * not filter there; applying the filter on both Dashboard modes would have
   * broken that agreement had the popover kept reading this component's view
   * classification. The pinned equality test is in `ManagePace.test.jsx`.
   */
  const paceImpact = useMemo(
    () =>
      classifyRoom(envelope?.squads, envelope?.capsules, pace?.resolvedLevel),
    [envelope?.squads, envelope?.capsules, pace?.resolvedLevel],
  );

  const navLocked = shell?.onboardingRequired === true;
  const contexts = Array.isArray(shell?.contexts) ? shell.contexts : [];
  const eligibility = envelope
    ? evaluatePaceWriteEligibility({
        envelope,
        contexts,
        facilitatorRoleId: shell?.facilitatorRoleId,
      })
    : null;

  const stored = pace?.storedLevel;
  const resolved = pace?.resolvedLevel;
  const orderedCapsules = classification.topology.capsules;
  const highest = orderedCapsules.length
    ? orderedCapsules[orderedCapsules.length - 1].levelNumber
    : null;

  return (
    <SurfaceCard
      as="section"
      tier="lifted"
      bleed
      /* THE CHROME TAKES THE DEPTH TOO (FE-9.8e, P30). The band is the first
         surface on every OS page, so a band still lit from one corner while the
         content under it is lit from two would read as the chrome belonging to
         an older version of the product. `bleed` keeps `overflow-hidden`, so the
         two radials are clipped at the band's own edges exactly as they are on a
         boxed card; what changes is that the band is wide and short, so the two
         corners read as the strip being lit from each END rather than from above
         and below.

         NO INTERACTION WITH ANY ANIMATION HERE. `fos-lens-land` is the matrix
         cell's, on Coach, and it is not mounted inside this band; the band's own
         `fos-entry-command` is a one-shot entry on the band itself and the
         lights are static children of it, so nothing standing moves. */
      depth="additive"
      aria-label="dashboard controls"
      className="dashboard-command-band fos-entry-command px-3 py-4 motion-reduce:animate-none sm:px-6"
      contentClassName="grid items-center gap-4 lg:grid-cols-[auto_minmax(160px,0.35fr)_minmax(300px,1fr)]"
    >
      <>
        <FacilitatorDashboardTabs />

        {!envelope || shell?.status === "loading" ? (
          /* ONE SENTENCE, NOT ONE SENTENCE IN A BOX (S-1, P1). A card is
             earned by an object a facilitator can act on; a status line is not
             one. This read as a third plane inside the band, which is itself
             already a lifted surface, so the band's own loading state looked
             like a component. It keeps `role="status"`, its grid position and
             both strings, and sits on the band's surface as the overline plus
             the sentence it always was. */
          <div role="status" className="min-w-0 lg:col-span-2">
            <p className="text-xs font-semibold uppercase tracking-button text-lv4">
              room controls
            </p>
            <p className="mt-1 text-sm text-lv2">
              {shell?.status === "loading"
                ? "loading this room's pace and pulse"
                : shell?.status === "error"
                  ? "this room's pace and pulse are unavailable"
                  : "no room context is available yet"}
            </p>
          </div>
        ) : (
          <>
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-button text-lv4">
                teacher setting
              </p>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                {/* THE STAT SCALE, AND NOT ONE WORD MOVES (S-3, P14). The
                    overline directly above is already the 11px uppercase label
                    a stat run wants; what was missing was the number. Every
                    string, its order and its spacing are byte-identical, which
                    is what keeps `FacilitatorShell.test.jsx`'s
                    `toHaveTextContent("level 2 of 2")` green: only the type
                    size of the numerals changes, so this is presentation and
                    not copy. `tabular-nums` is what stops the run jittering
                    when the level ticks from 9 to 10.

                    THE NOT-RECORDED BRANCH KEEPS ITS SIZE, deliberately. It is
                    a sentence, not a signal, and setting a sentence at the stat
                    scale is exactly the "that size appears nowhere else" rule
                    being broken by the state with no number in it. */}
                <p className="text-md text-lv0" data-testid="expected-pace">
                  {stored == null ? (
                    statusTerm("pace_not_recorded")
                  ) : (
                    <>
                      level{" "}
                      <span className="text-[22px] font-semibold leading-none tabular-nums">
                        {stored}
                      </span>
                      {highest ? (
                        <>
                          {" "}
                          of <span className="tabular-nums">{highest}</span>
                        </>
                      ) : null}
                    </>
                  )}
                </p>
                {eligibility?.allowed ? (
                  navLocked ? (
                    /* THE LEVEL SWITCHER IS DISABLED DURING SETUP, AND IT IS
                       STILL ON SCREEN. Same ruling as the two tabs: a
                       facilitator part way through the setup rail sees the
                       control they will have, dimmed, with the one line saying
                       why, rather than a band that grows a control later and
                       looks like it changed.

                       `blocked` RATHER THAN `disabled`, so the node keeps its
                       place in the tab order and can still be read; `Button`'s
                       own docblock argues that case at length. The popover is
                       not lazily loaded here at all, which is the point: there
                       is nothing to open. */
                    <Button
                      size="sm"
                      variant="secondary"
                      blocked
                      aria-label="change expected pace"
                      aria-describedby={NAV_LOCK_EXPLAINER_ID}
                      data-testid="pace-control-locked"
                    >
                      change
                    </Button>
                  ) : (
                    <Suspense
                      fallback={<Skeleton className="h-7 w-16 rounded-full" />}
                    >
                      <PaceControlPopover
                        envelope={envelope}
                        classification={paceImpact}
                        refreshSnapshot={shell.refreshSnapshot}
                        facilitatorRoleId={shell?.facilitatorRoleId}
                      />
                    </Suspense>
                  )
                ) : null}
              </div>
              {/* THE LENS WAS EXPLAINED SIX TIMES ABOVE THE FOLD, and two of
                  the six were the same sentence about 300px apart: this line
                  and the room reading's restatement of it. The overline
                  directly above this pill already says `teacher setting`, which
                  is the same distinction in two words and in the position where
                  a label is read, so the sentence is cut here and the room
                  reading spends its line on the one thing nothing on the
                  surface said (D1, D2).

                  THE OUT-OF-RANGE BRANCH IS NOT DUPLICATION AND STAYS. "stored
                  level 9, resolved to level 8" is the only place the surface
                  admits that the number it is comparing against is not the
                  number the teacher set. */}
              {pace?.outOfRange ? (
                <p className="mt-1 text-xs text-lv3">
                  {`stored level ${stored}, resolved to level ${resolved}`}
                </p>
              ) : null}
              {!eligibility?.allowed ? (
                <p className="mt-1 text-xs text-lv4">{eligibility?.reason}</p>
              ) : null}
            </div>

            <RoomPulse
              classification={classification}
              expectedLevel={resolved}
            />
          </>
        )}
      </>
    </SurfaceCard>
  );
};

export default FacilitatorCommandBand;
